import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SendNotificationFunction } from ".";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Autocomplete, TextField } from "@mui/material";
import { languages } from "../video";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { isTitleValid, isURLValid } from "../../utils/rgexValidation";
import { AdminPageLayout, AdminSectionCard } from "../../components/admin/AdminPageLayout";
import { AdminAction, AdminFormFooter } from "../../components/admin";

export default function SendNotification() {
  const navigate = useNavigate();
  const [title, setTitle] = useState(null);
  const [url, setURL] = useState("");
  const [message, setmessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState("English");
  const [toDate, setToDate] = useState(new Date());
  const [fromDate, setFromDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSendNotification = async () => {
    if (url !== "") {
      if (!isURLValid(url)) {
        toast.error("Invalid URL format!", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: true,
        });
        setLoading(false);
        return;
      }
    }

    try {
      const data = {
        title: title,
        message: message,
        url: url,
        language: lang,
        dateFrom: fromDate,
        dateTo: toDate,
      };
      const response = await SendNotificationFunction(data);
      if (response) {
        navigate(`/notifications`, { state: { result: data }, replace: true });
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      if (message === null) {
        toast.error("Message is required!", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: true,
        });
      } else if (title === null) {
        toast.error("Title is required!", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: true,
        });
      } else if (charCount >= 3900) {
        toast.error("Message size is too large!", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: true,
        });
      } else {
        toast.error("Something went Wrong!", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: true,
        });
      }
      console.log(error);
    }
  };

  const handleTitle = (event) => {
    setTitle(event.target.value);
  };

  const handleURL = (event) => {
    setURL(event.target.value);
  };

  const handlemessage = (event) => {
    setmessage(event.target.value);
    const text = event.target.value;
    setCharCount(text.length);
  };

  const handleLanguage = (value) => {
    if (value) setLang(value.name);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
    setShowDatePicker(false);
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
    setShowDatePicker(false);
  };

  return (
    <AdminPageLayout
      eyebrow="Communication"
      title="Send Notification"
      description="Compose platform notifications with scheduling, language selection, and shareable links."
    >
      <AdminSectionCard
        title="Notification composer"
        description="Fill in the message details below and send directly to your users."
      >
        <div className="admin-form-body overflow-y-auto max-h-[70vh]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                placeholder="Enter notification title"
                className="block w-full rounded-xl border-0 py-2.5 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 placeholder:text-stone-400 focus:ring-2 focus:ring-inset focus:ring-stone-900 sm:text-sm sm:leading-6 transition-shadow px-4"
                onChange={handleTitle}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                URL <span className="text-stone-400">(optional)</span>
              </label>
              <input
                type="text"
                placeholder="https://example.com"
                className="block w-full rounded-xl border-0 py-2.5 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 placeholder:text-stone-400 focus:ring-2 focus:ring-inset focus:ring-stone-900 sm:text-sm sm:leading-6 transition-shadow px-4"
                onChange={handleURL}
              />
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                Date From
              </label>
              <div className="block w-full rounded-xl border-0 py-2 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 px-4">
                <DatePicker
                  disabled
                  selected={fromDate}
                  onChange={handleFromDateChange}
                  dateFormat="yyyy/MM/dd"
                  className="w-full outline-none bg-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                Date To
              </label>
              <div className="block w-full rounded-xl border-0 py-2 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 px-4">
                <DatePicker
                  selected={toDate}
                  onChange={handleToDateChange}
                  dateFormat="yyyy/MM/dd"
                  className="w-full outline-none bg-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                Language
              </label>
              <Autocomplete
                disablePortal
                id="language"
                options={languages}
                defaultValue={{ id: 1, name: "English" }}
                getOptionLabel={(option) => option.name}
                onChange={(event, newValue) => handleLanguage(newValue)}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '0.5rem' }
                }}
                renderInput={(params) => <TextField {...params} label="Language" />}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                Description *
              </label>
              <textarea
                className="block w-full rounded-xl border-0 py-2.5 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 placeholder:text-stone-400 focus:ring-2 focus:ring-inset focus:ring-stone-900 sm:text-sm sm:leading-6 transition-shadow px-4 min-h-[120px]"
                placeholder="Enter notification message"
                id="message"
                name="message"
                rows="4"
                onChange={handlemessage}
                required
              />
              <p className="mt-1 text-xs text-stone-400">{charCount} characters</p>
            </div>
          </div>
        </div>

        <AdminFormFooter>
          <AdminAction onClick={handleSendNotification} loading={loading}>
            Send Notification
          </AdminAction>
        </AdminFormFooter>
      </AdminSectionCard>
    </AdminPageLayout>
  );
}
